/**
 * Utility functions for product-related components
 */

/**
 * Determines the number of items to display per page based on screen width
 * @returns {number} The number of items per page
 */
export const getItemsPerPage = (itemsPerRowParam) => {
    const w = window.innerWidth || 0;
    const h = window.innerHeight || 0;
    const itemsPerRow = itemsPerRowParam || getItemsPerRow();

    const gap = 12;
    const textExtra = 80;
    const columnWidth = itemsPerRow > 0 ? w / itemsPerRow : w;
    const cardHeight = columnWidth + textExtra;

    const rowsThatFit = Math.max(1, Math.floor(h / (cardHeight + gap)));
    const count = itemsPerRow * rowsThatFit;

    return Math.max(count, itemsPerRow || 1);
};

/**
 * Determines how many items fit per row based on Tailwind breakpoints used in the grid
 * Matches classes: min-[350px]:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6
 * @returns {number}
 */
export const getItemsPerRow = () => {
    const w = window.innerWidth || 0;
    const h = window.innerHeight || 0;
    const portrait = h > w;

    if (portrait) {
        if (w >= 1024) return 3;
        if (w >= 768) return 2;
        if (w >= 350) return 2;
        return 1;
    }

    if (w >= 1536) return 5;
    if (w >= 1280) return 4;
    if (w >= 1024) return 3;
    if (w >= 768) return 3;
    if (w >= 640) return 2;
    if (w >= 350) return 2;
    return 1;
};

/**
 * Sets up a resize event listener to update items per page
 * @param {Function} setItemsPerPage - State setter function for itemsPerPage
 * @returns {Function} Cleanup function to remove event listener
 */
export const setupResponsiveGrid = (setItemsPerPage) => {
    const handleResize = () => setItemsPerPage(getItemsPerPage());

    window.addEventListener("resize", handleResize);

    return () => {
        window.removeEventListener("resize", handleResize);
    };
};

/**
 * Sets up a resize event listener to update items per row (columns)
 * @param {Function} setItemsPerRow - State setter for itemsPerRow
 * @returns {Function} Cleanup function
 */
export const setupResponsiveColumns = (setItemsPerRow) => {
    const handleResize = () => setItemsPerRow(getItemsPerRow());

    window.addEventListener("resize", handleResize);

    return () => {
        window.removeEventListener("resize", handleResize);
    };
};

/**
 * Calculates the total number of pages based on total items and items per page
 * @param {Array} items - The array of items
 * @param {number} itemsPerPage - The number of items per page
 * @returns {number} The total number of pages
 */
export const calculateTotalPages = (items, itemsPerPage) => {
    return Math.ceil(items.length / itemsPerPage);
};

/**
 * Determines the dynamic number of rows to load per lazy batch based on aspect ratio.
 * Default is 2; increase when height > width (portrait) to utilize vertical space.
 * @returns {number}
 */
export const getRowsPerLoad = () => {
    if (typeof window === 'undefined') return 2;
    const w = window.innerWidth || 0;
    const h = window.innerHeight || 0;
    if (h > w) {
        return 3;
    }
    return 2;
};

/**
 * Sets up a resize event listener to update rowsPerLoad dynamically.
 * @param {Function} setRowsPerLoad
 * @returns {Function} Cleanup function
 */
export const setupResponsiveRows = (setRowsPerLoad) => {
    const handleResize = () => setRowsPerLoad(getRowsPerLoad());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
};
